import React, { Suspense, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import getTheme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeContextProvider, useThemeMode } from './contexts/ThemeContext';
import { DemoRoleProvider } from './contexts/DemoRoleContext';

// Auth components
import LoginPage from './components/Auth/LoginPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';

// Layout
import AppLayout from './components/Layout/AppLayout';
import ToolWrapper from './components/Layout/ToolWrapper';

// Pages
import Dashboard from './pages/Dashboard';
import EntryPage from './platform/entry/EntryPage';
import LegacyDrewRedirect from './platform/navigation/LegacyDrewRedirect';
import Signup from './pages/Signup';
import Guide from './pages/Guide';
import Help from './pages/Help';
import ApiDocs from './pages/ApiDocs';
import SettingsPage from './pages/SettingsPage';
import Admin from './pages/Admin';
import NotFound from './pages/NotFound';

// Drift & Accumulation tool — existing pages
import DriftDashboard from './assistants/drew/DriftDashboard';
import Surveys from './assistants/drew/pages/Surveys';
import SurveyDetail from './assistants/drew/pages/SurveyDetail';
import TakeSurvey from './assistants/drew/pages/TakeSurvey';
import REFReports from './assistants/drew/pages/REFReports';
import Actions from './assistants/drew/pages/Actions';
import CaseStudies from './assistants/drew/pages/CaseStudies';
import CaseStudyDetail from './assistants/drew/pages/CaseStudyDetail';
import Documents from './assistants/drew/pages/Documents';

// Tim's dream-flow — Branch 1 (role dashboards)
import ITMDashboard from './assistants/drew/pages/roles/ITMDashboard';
import FLSDashboard from './assistants/drew/pages/roles/FLSDashboard';
import DHDashboard from './assistants/drew/pages/roles/DHDashboard';
import ExecutiveDashboard from './assistants/drew/pages/roles/ExecutiveDashboard';
import OrgITPortal from './assistants/drew/pages/roles/OrgITPortal';

// Tim's dream-flow — Branch 2 (rollup chain)
import TRLEntry from './assistants/drew/pages/rollup/TRLEntry';
import ReviewQueue from './assistants/drew/pages/rollup/ReviewQueue';
import SeverityCalibration from './assistants/drew/pages/rollup/SeverityCalibration';
import SiteDashboard from './assistants/drew/pages/rollup/SiteDashboard';
import RegionalRollup from './assistants/drew/pages/rollup/RegionalRollup';
import EnterpriseRollup from './assistants/drew/pages/rollup/EnterpriseRollup';

// Tim's dream-flow — Branch 3 (IA expansion)
import ITPortalHub from './assistants/drew/pages/ia/ITPortalHub';
import TRLSystem from './assistants/drew/pages/ia/TRLSystem';
import Comparisons from './assistants/drew/pages/ia/Comparisons';
import HPBarometer from './assistants/hugo/HPBarometer';
import ComingWithHugo from './assistants/hugo/ComingWithHugo';
import { FEATURE_HUGO } from './config/features';
import HealthMessages from './assistants/drew/pages/ia/HealthMessages';
import ReportsOnDemand from './assistants/drew/pages/ia/ReportsOnDemand';
import DemoAccessGate from './assistants/drew/components/DemoAccessGate';
import Loading from './components/Common/Loading';

// Dev-only playground for the dashboard widget framework (Beta-1 plan P3-15). The dynamic import
// sits behind `import.meta.env.DEV`, so production builds drop the chunk entirely.
const WidgetPlayground = import.meta.env.DEV ? React.lazy(() => import('./platform/widgets/dev/WidgetPlayground.jsx')) : null;

const TOOL_SLUG = 'drift-accumulation';
const TOOL_NAME = 'DREW';

// Sidebar items (kept for legacy ToolWrapper API; the actual sidebar reads route paths directly now).
const driftSidebarItems = [
  { text: 'OE Dashboard', icon: 'Dashboard', path: '/drew' },
  { text: 'Surveys', icon: 'Assignment', path: '/drew/surveys' },
  { text: 'Learning Library', icon: 'MenuBook', path: '/drew/case-studies' },
  { text: 'Reports', icon: 'Assessment', path: '/drew/reports' },
  { text: 'Actions', icon: 'PlaylistAddCheck', path: '/drew/actions' },
  { text: 'Document Upload Portal', icon: 'Description', path: '/drew/documents' },
  { text: 'Org Settings', icon: 'Business', path: '/drew/settings' },
];

/**
 * Helper that wraps every D&A tool page in the same ToolWrapper. Drastically
 * cuts the route boilerplate now that we have ~20 tool pages.
 */
function toolRoute(path, Component) {
  return (
    <Route
      key={path}
      path={path}
      element={
        <ToolWrapper toolSlug={TOOL_SLUG} toolName={TOOL_NAME} sidebarItems={driftSidebarItems}>
          <DemoAccessGate path={path}>
            <Component />
          </DemoAccessGate>
        </ToolWrapper>
      }
    />
  );
}

function AppContent() {
  const { themeMode } = useThemeMode();
  const theme = useMemo(() => getTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <UserProvider>
            <NotificationProvider>
              <DemoRoleProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/guide" element={<Guide />} />
                  {WidgetPlayground && (
                    <Route
                      path="/dev/widgets"
                      element={
                        <Suspense fallback={<Loading variant="fullpage" />}>
                          <WidgetPlayground />
                        </Suspense>
                      }
                    />
                  )}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    {/* P6-01: the entry page is home; the REPSit dashboard (welcome page) stays one click away */}
                    <Route index element={<EntryPage />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="settings/:tab" element={<SettingsPage />} />
                    <Route path="help" element={<Help />} />
                    <Route path="api-docs" element={<ApiDocs />} />
                    <Route
                      path="admin"
                      element={
                        <AdminRoute>
                          <Admin />
                        </AdminRoute>
                      }
                    />

                    {/* Drift & Accumulation — existing core pages */}
                    {toolRoute('drew', DriftDashboard)}
                    {toolRoute('drew/surveys', Surveys)}
                    {toolRoute('drew/surveys/:id', SurveyDetail)}
                    {toolRoute('drew/surveys/:id/take', TakeSurvey)}
                    {toolRoute('drew/reports', REFReports)}
                    {toolRoute('drew/actions', Actions)}
                    {toolRoute('drew/case-studies', CaseStudies)}
                    {toolRoute('drew/case-studies/:id', CaseStudyDetail)}
                    {toolRoute('drew/documents', Documents)}
                    {/* P4-01: Org Settings moved to Settings → Organization; old links and tours redirect */}
                    <Route path="drew/settings" element={<Navigate to="/settings/organization" replace />} />

                    {/* Branch 1 — Access Levels */}
                    {toolRoute('drew/role/itm', ITMDashboard)}
                    {toolRoute('drew/role/fls', FLSDashboard)}
                    {toolRoute('drew/role/dh', DHDashboard)}
                    {toolRoute('drew/role/executive', ExecutiveDashboard)}
                    {toolRoute('drew/role/org-it', OrgITPortal)}

                    {/* Branch 2 — Data Input / Rollup */}
                    {toolRoute('drew/rollup/trl-entry', TRLEntry)}
                    {toolRoute('drew/rollup/review-queue', ReviewQueue)}
                    {toolRoute('drew/rollup/severity-calibration', SeverityCalibration)}
                    {toolRoute('drew/rollup/site', SiteDashboard)}
                    {toolRoute('drew/rollup/regional', RegionalRollup)}
                    {toolRoute('drew/rollup/enterprise', EnterpriseRollup)}

                    {/* Branch 3 — Intelligent Assistant */}
                    {toolRoute('drew/ia/it-portal', ITPortalHub)}
                    {toolRoute('drew/ia/trl-system', TRLSystem)}
                    {toolRoute('drew/ia/comparisons', Comparisons)}
                    {/* P6-15: the Barometer is HUGO's first page; the old DREW URL redirects (emails, bookmarks) */}
                    <Route path="drew/ia/barometer" element={<Navigate to="/hugo/barometer" replace />} />
                    {toolRoute('hugo/barometer', FEATURE_HUGO ? HPBarometer : ComingWithHugo)}
                    {toolRoute('drew/ia/health-messages', HealthMessages)}
                    {toolRoute('drew/ia/reports-on-demand', ReportsOnDemand)}
                    {/* Unknown app URLs render inside the layout, so a mistyped link is never a dead end
                        (UI review 2026-09-21 §8). */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  {/* P6-14: legacy DREW URLs (emails, tours, bookmarks) → /drew/... */}
                  <Route path="tools/drift-accumulation/*" element={<LegacyDrewRedirect />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </DemoRoleProvider>
            </NotificationProvider>
          </UserProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeContextProvider>
      <AppContent />
    </ThemeContextProvider>
  );
}

export default App;
